import { useEffect, useState } from "react";
import { isAbortError } from "../../api/client";
import { fetchErganiHistory, fetchErganiImports, uploadErganiXml } from "../../api/ergani";
import { useErganiSlots } from "../../hooks/useErganiSlots";
import { rosterScheduleKey } from "../../employeeMatch";
import { slotKey } from "../../lib/softoneSlotSync";
import { isSelectableStore } from "../../theme";

export function ErganiXmlPanel({ lang, store, weekStart, weekDates, staff }) {
  const t = (en, gr) => (lang === "gr" ? gr : en);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [summary, setSummary] = useState(null);
  const [imports, setImports] = useState([]);
  const [openKey, setOpenKey] = useState("");
  const [history, setHistory] = useState([]);
  const ergani = useErganiSlots({
    store,
    weekStartISO: weekStart,
    enabled: isSelectableStore(store),
  });

  useEffect(() => {
    const controller = new AbortController();
    fetchErganiImports(8, { signal: controller.signal })
      .then((data) => {
        if (!controller.signal.aborted) setImports(data.imports || []);
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        setImports([]);
      });
    return () => controller.abort();
  }, [summary]);

  async function acceptFile(file) {
    if (!file) return;
    if (!String(file.name || "").toLowerCase().endsWith(".xml")) {
      setUploadError(t("Drop a .xml file", "Ρίξτε αρχείο .xml"));
      return;
    }
    setUploading(true);
    setUploadError("");
    try {
      const result = await uploadErganiXml(file);
      setSummary(result);
      await ergani.reload();
    } catch (err) {
      setUploadError(err.message || t("Upload failed", "Η αποστολή απέτυχε"));
    } finally {
      setUploading(false);
    }
  }

  async function toggleHistory(code, date) {
    const key = slotKey(code, date);
    if (openKey === key) {
      setOpenKey("");
      setHistory([]);
      return;
    }
    setOpenKey(key);
    try {
      const data = await fetchErganiHistory(code, date);
      setHistory(data.versions || []);
    } catch (err) {
      setHistory([]);
      setUploadError(err.message || "");
    }
  }

  const rosterCodes = new Set((staff || []).map((emp) => String(rosterScheduleKey(emp) || "").trim()).filter(Boolean));

  return (
    <div>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          acceptFile(e.dataTransfer.files?.[0]);
        }}
        style={{
          border: `2px dashed ${dragOver ? "var(--green)" : "var(--border)"}`,
          borderRadius: 14,
          padding: 22,
          textAlign: "center",
          background: dragOver ? "var(--green-light)" : "var(--surface)",
          marginBottom: 14,
        }}
      >
        <div style={{ fontWeight: 800, color: "var(--text-primary)" }}>
          {t("Drop Ergani WTODAILY.xml", "Ρίξτε WTODAILY.xml Εργάνη")}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>
          {t(
            "Adds or updates declared days. Days missing from the file stay as they were.",
            "Προσθέτει ή ενημερώνει ημέρες. Όσες λείπουν από το αρχείο μένουν ως έχουν."
          )}
        </div>
        <label style={{ display: "inline-block", marginTop: 12, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          {uploading ? t("Reading…", "Ανάγνωση…") : t("Choose XML", "Επιλογή XML")}
          <input
            type="file"
            accept=".xml,text/xml,application/xml"
            hidden
            disabled={uploading}
            onChange={(e) => {
              acceptFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </label>
        {uploadError ? (
          <div style={{ marginTop: 10, color: "var(--danger)", fontSize: 12, fontWeight: 700 }}>{uploadError}</div>
        ) : null}
      </div>

      {summary ? (
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: 14, marginBottom: 14, fontSize: 13 }}>
          <div style={{ fontWeight: 800 }}>
            {summary.duplicate
              ? t("Already imported", "Ήδη εισηγμένο")
              : t("Imported", "Εισήχθη")}
            {summary.filename ? ` · ${summary.filename}` : ""}
          </div>
          <div style={{ marginTop: 6, color: "var(--text-secondary)" }}>
            {summary.period_from || "—"} – {summary.period_to || "—"}
            {" · "}
            {t(`${summary.employee_count || 0} people`, `${summary.employee_count || 0} άτομα`)}
            {" · "}
            {t(`${summary.inserted || 0} new`, `${summary.inserted || 0} νέα`)}
            {" · "}
            {t(`${summary.updated || 0} updated`, `${summary.updated || 0} ενημερώσεις`)}
            {" · "}
            {t(`${summary.unchanged || 0} unchanged`, `${summary.unchanged || 0} ίδια`)}
          </div>
        </div>
      ) : null}

      {imports[0] && !summary ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>
          {t("Last file", "Τελευταίο αρχείο")}: {imports[0].filename} · {imports[0].period_from} – {imports[0].period_to}
        </div>
      ) : null}

      {!isSelectableStore(store) ? (
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{t("Select a store", "Επιλέξτε κατάστημα")}</div>
      ) : (
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: 8 }}>{t("Employee", "Υπάλληλος")}</th>
                {(weekDates || []).map((d) => (
                  <th key={d.toISOString()} style={{ padding: 8 }}>{d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(staff || []).map((emp) => {
                const code = String(rosterScheduleKey(emp) || "").trim();
                return (
                  <tr key={code || emp.id}>
                    <td style={{ padding: 8, fontWeight: 700 }}>{emp.name || code}</td>
                    {(weekDates || []).map((d) => {
                      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                      const row = code ? ergani.byKey[slotKey(code, iso)] : null;
                      const unmatched = row && !rosterCodes.has(code);
                      const key = slotKey(code, iso);
                      return (
                        <td key={iso} style={{ padding: 6, textAlign: "center" }}>
                          <button type="button" onClick={() => code && toggleHistory(code, iso)} style={{ border: "none", background: "transparent", cursor: "pointer", fontWeight: 700 }}>
                            {row ? row.shift_value : "—"}
                            {row?.version_count > 1 ? ` · ${row.version_count}` : ""}
                          </button>
                          {unmatched ? <div style={{ fontSize: 10 }}>{t("Unmatched AFM", "ΑΦΜ εκτός λίστας")}</div> : null}
                          {openKey === key ? (
                            <div style={{ textAlign: "left", marginTop: 6 }}>
                              {history.map((version, i) => (
                                <div key={`${version.source_import_id}-${i}`} style={{ fontSize: 10, color: "var(--text-muted)" }}>
                                  {version.filename || t("XML", "XML")} · {version.shift_value}
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {(ergani.slots || []).filter((row) => !rosterCodes.has(String(row.employee_code))).map((row) => (
            <div key={`${row.employee_code}-${row.date}`} style={{ padding: 8, fontSize: 12 }}>
              {t("Unmatched AFM", "ΑΦΜ εκτός λίστας")} {row.employee_code} · {row.date} · {row.shift_value}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
